import React, { useCallback } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useClaims } from '@verzekeringapp-mono/shared/src/hooks/use_claims'
import { Link, useNavigate } from 'react-router-dom'
import { IClaim } from '@verzekeringapp-mono/shared/src/models/claim'
import { format } from 'date-fns'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useThemeColorContext } from '~/providers/ThemeColorProvider'
import { useMemo } from 'react'
import { resolveClaimStatusColor, rgbaFromHex } from '@verzekeringapp-mono/shared/src/utils/claim_status_color'

interface IProps {
  claim: IClaim
}

const icons: { [key: string]: IconProp } = {
  Concept: 'pencil',
  'In behandeling': 'clock',
  Afgehandeld: 'check',
  'Gemeld bij adviseur': 'user-tie'
}

const ClaimIcon = (
  {
    claim,
    className,
    statusColor
  }: {
    claim: IClaim
    className?: string
    statusColor: string
  }
) => (
  <div
    className={`flex h-[50px] w-[50px] items-center justify-center rounded-full ${className ?? ''}`}
    style={{ backgroundColor: rgbaFromHex(statusColor, 0.1) }}
  >
    <FontAwesomeIcon
      icon={icons[claim.status] ?? 'circle'}
      size='lg'
      style={{ color: statusColor }}
    />
  </div>
)

const ClaimCard: React.FC<IProps> = ({ claim }) => {
  const { themeColors, themeMode } = useThemeColorContext()
  const { deleteClaim } = useClaims()
  const navigate = useNavigate()
  const statusColor = useMemo(
    () => resolveClaimStatusColor(claim.status, claim.statusColor, themeColors, themeMode),
    [claim.status, claim.statusColor, themeColors, themeMode]
  )

  const onPressRemove = useCallback(
    async (temporaryClaimReference: string) => {
      const confirmed = window.confirm(
        'Weet je zeker dat je de concept schademelding wilt verwijderen?'
      )

      if (confirmed) {
        const success = await deleteClaim(temporaryClaimReference)

        if (success) {
          navigate('/dossier/mijn-schades')
        }
      }
    },
    [claim, deleteClaim]
  )

  return (
    <div className='mb-4 flex flex-col justify-center gap-8 rounded border border-border-subtle bg-surface-card p-4 md:p-8'>
      <Link
        to={`/dossier/mijn-schades/${
          claim?.id
        }`}
      >
        <div className='hidden md:flex flex-col gap-4'>
          <div className='flex justify-between gap-4'>
            <p className='text-xl'>{claim.productName}</p>

            <div
              className='flex items-center justify-center px-4 rounded-full'
              style={{ backgroundColor: statusColor }}
            >
              <p className='paragraph-light text-center text-text-on-neutral-strong'>{claim.status}</p>
            </div>
          </div>

          <div className='flex gap-4 flex-1 flex-col lg:flex-row'>
            <div className='w-1/4'>
              <p className='paragraph-light'>Schadedatum</p>

              {claim.dateOfLoss && (
                <p>{format(new Date(claim.dateOfLoss), 'dd-MM-yyyy')}</p>
              )}
            </div>

            <div className='w-1/4'>
              <p className='paragraph-light'>Polisnummer</p>

              <p>{claim.policyNumber}</p>
            </div>

            <div className='w-1/4'>
              <p className='paragraph-light'>Verzekering</p>

              <p>{claim.productName}</p>
            </div>
          </div>

          <div>
            <p className='paragraph-light'>Toelichting status</p>

            <p>{claim.statusDescription}</p>
          </div>

          <div className='flex justify-between'>
            <a
              href={`/dossier/mijn-schades/${claim?.id}`}
              className='flex items-center gap-2 text-sm text-text-link'
            >
              <FontAwesomeIcon
                icon={['fal', 'arrow-right']}
                className='text-icon-brand'
                size='sm'
              />
              Bekijk schademelding
            </a>

            {claim?.status === 'Concept' && (
              <button
                type='button'
                onClick={() =>
                  onPressRemove(claim?.id || '')
                }
                className='text-status-danger-foreground'
              >
                <FontAwesomeIcon
                  icon='trash'
                />
              </button>
            )}
          </div>
        </div>

        <div className='flex items-center justify-between flex-1 md:hidden'>
          <div className='flex flex-row items-center'>
            <ClaimIcon
              claim={claim}
              className='mr-4'
              statusColor={statusColor}
            />

            <div>
              <p className='font-bold text-text-primary'>{claim.productName}</p>

              <small className='text-text-muted'>{claim.policyNumber}</small>

              <p
                className='text-sm'
                style={{ color: statusColor }}
              >
                {claim.status}
              </p>
            </div>
          </div>

          <div>
            <FontAwesomeIcon
              icon={['fal', 'chevron-right']}
              size='lg'
              className='text-icon-secondary'
            />
          </div>
        </div>
      </Link>
    </div>
  )
}

export default ClaimCard
